import { OddsFormat } from '@bpos';
import {
    Participant,
    ParticipantMarketStatus,
    ParticipantOption,
    ParticipantPriceStatus,
    ParticipantPriceType,
    ParticipantStatus,
} from '@cds/betting-offer';
import { MessageEnvelope } from '@cds/push';
import { isParticipantUpdateEnvelope } from '@frontend/sports/content-distribution/feature';
import { OddsOperations, emptyOdds } from '@frontend/sports/odds/feature';

import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV2ParticipantPick } from '../../../core/picks/betslip-v2-participant-pick';
import { IBetslipV2PickParticipant, IPrice } from '../../../core/picks/pick-models';
import { participantOptionPriceToBetslipPrice } from '../../../core/price';

export class BetslipV2ParticipantPickUpdater {
    updateFromMessage(pick: BetslipV2ParticipantPick, message: MessageEnvelope, oddsFormat: OddsFormat): BetslipPick {
        if (
            isParticipantUpdateEnvelope(message) &&
            message.payload.fixtureId === pick.fixture.fixtureId &&
            pick.participants.some((p) => p.fixtureParticipantId === message.payload.participant.id)
        ) {
            const updatedPick = pick.copy();
            const command = message.payload;
            const pickParticipant = updatedPick.participants.find((p) => p.fixtureParticipantId === message.payload.participant.id)!;
            this.updateFromParticipant(updatedPick, pickParticipant, command.participant, oddsFormat);

            return updatedPick;
        }

        return pick;
    }

    protected updateFromParticipant(
        pick: BetslipV2ParticipantPick,
        thisParticipant: IBetslipV2PickParticipant,
        fixtureParticipant: Participant,
        oddsFormat: OddsFormat,
    ): void {
        thisParticipant.status = fixtureParticipant.status || ParticipantStatus.NotDefined;

        const participantOptions = fixtureParticipant.options.filter((o) => o.marketId === pick.market.id);
        this.updateFromParticipantOptions(pick, participantOptions);

        for (const price of thisParticipant.prices) {
            const fixtureOption = participantOptions.find((o) => o.price.id === price.id);
            this.updateFromPrice(pick, price, oddsFormat, fixtureOption);
        }
        // prices to add if any new for current market id.
        const missingOptions = participantOptions.filter((o) => thisParticipant.prices.every((p) => p.id !== o.price.id));
        for (const option of missingOptions) {
            this.addPriceFromOption(thisParticipant, option);
        }
    }

    protected updateFromParticipantOptions(pick: BetslipV2ParticipantPick, options: ParticipantOption[]): void {
        const currentMarketOptions = options.filter((o) => o.marketType === pick.market.marketType);
        pick.market.isVisible =
            !!currentMarketOptions.length && currentMarketOptions.every((o) => o.marketStatus === ParticipantMarketStatus.Visible);
    }

    protected updateFromPrice(pick: BetslipV2ParticipantPick, thisPrice: IPrice, oddsFormat: OddsFormat, fixtureOption?: ParticipantOption): void {
        if (!fixtureOption) {
            pick.priceHistory.push({ ...thisPrice });
            thisPrice.isVisible = false;
        } else {
            const newPrice = {
                isVisible:
                    fixtureOption.marketStatus === ParticipantMarketStatus.Visible &&
                    fixtureOption.participantPriceStatus === ParticipantPriceStatus.Visible,
                nativeOdds: emptyOdds,
            };
            if (fixtureOption.priceType === ParticipantPriceType.Fixed) {
                // Set odds only when priceType is fixed.
                newPrice.nativeOdds = OddsOperations.createOdds(fixtureOption.price);
                newPrice.isVisible = newPrice.isVisible && OddsOperations.isOddsValid(newPrice.nativeOdds);
            }
            if (!OddsOperations.equal(newPrice.nativeOdds, thisPrice.nativeOdds, oddsFormat) || thisPrice.isVisible !== newPrice.isVisible) {
                pick.priceHistory.push({ ...thisPrice });
            }
            thisPrice.isVisible = newPrice.isVisible;
            thisPrice.nativeOdds = newPrice.nativeOdds;
        }
    }

    protected addPriceFromOption(participant: IBetslipV2PickParticipant, option: ParticipantOption): void {
        const price = participantOptionPriceToBetslipPrice(option);
        participant.prices.push(price);
    }
}
